import type { Params } from '../params/params.types'

export const INLINE_GRID_PROTOCOL = 'classy-inline-grid'

export const INLINE_GRID_MESSAGE_TYPE = {
  ANALYTICS: 'CLASSY_INLINE_GRID_ANALYTICS',
  ERROR: 'CLASSY_INLINE_GRID_ERROR',
  HELLO: 'CLASSY_INLINE_GRID_HELLO',
  INITIALIZE: 'CLASSY_INLINE_GRID_INITIALIZE',
  READY: 'CLASSY_INLINE_GRID_READY',
  RESIZE: 'CLASSY_INLINE_GRID_RESIZE',
  SELECTION_UPDATE: 'CLASSY_INLINE_GRID_SELECTION_UPDATE',
  SUBMIT: 'CLASSY_INLINE_GRID_SUBMIT',
} as const

export type InlineGridMessageType =
  (typeof INLINE_GRID_MESSAGE_TYPE)[keyof typeof INLINE_GRID_MESSAGE_TYPE]

export type InlineGridFrequency =
  | 'one-time'
  | 'daily'
  | 'weekly'
  | 'bi-weekly'
  | 'monthly'
  | 'quarterly'
  | 'semi-annually'
  | 'yearly'

export interface InlineGridDedication {
  text: string
  type: 'honor' | 'memory'
}

export interface InlineGridSelection {
  amount: number
  currency: string
  dedication?: InlineGridDedication
  designation?: number
  frequency: InlineGridFrequency
  recurringEndDate?: string
}

export interface InlineGridSelectionUpdate {
  amount?: number
  currency?: string
  dedication?: InlineGridDedication | null
  designation?: number
  frequency?: InlineGridFrequency
  recurringEndDate?: string
}

export interface InlineGridLiveSelection {
  amount: number | null
  currency: string
  frequency: InlineGridFrequency
}

export type InlineGridInteractionEventName = 'Button Clicked' | 'Value Selected'

export const INLINE_GRID_INTERACTIONS = {
  'dedication start': 'Value Selected',
  'donation: dedication hidden': 'Value Selected',
  'donation: dedication start': 'Value Selected',
  'donation: ticker dismissed': 'Button Clicked',
  'donation ticker dismissed': 'Button Clicked',
} as const satisfies Record<string, InlineGridInteractionEventName>

export type InlineGridInteractionTrackingId = keyof typeof INLINE_GRID_INTERACTIONS

export interface InlineGridViewedEventData {
  donation_amount_default: number
  donation_amount_options: string
  donation_currency_default: string
  donation_currency_options: string
  donation_frequency_default: string
  donation_frequency_options: string
}

export type InlineGridAnalyticsPayload =
  | {
      action: 'amount_change'
      campaignId: string
      currency: string
      donationType: 'custom' | 'preset'
      fromAmount: number
      toAmount: number
    }
  | {
      action: 'currency_change'
      campaignId: string
      fromCurrency: string
      toCurrency: string
    }
  | {
      action: 'frequency_change'
      campaignId: string
      fromFrequency: InlineGridFrequency
      toFrequency: InlineGridFrequency
    }
  | {
      action: 'interaction'
      campaignId: string
      trackingId: InlineGridInteractionTrackingId
    }
  | ({ action: 'viewed'; campaignId: string } & InlineGridViewedEventData)

export type InlineGridErrorCode =
  | 'GRID_INITIALIZATION_FAILED'
  | 'GRID_NOT_INITIALIZED'
  | 'GRID_RENDER_FAILED'
  | 'GRID_RESIZE_FAILED'
  | 'GRID_SUBMIT_FAILED'

export type InlineGridErrorPhase = 'initialize' | 'render' | 'resize' | 'submit'

export type InlineGridErrorReason =
  | 'campaign_unavailable'
  | 'configuration_unavailable'
  | 'height_unavailable'
  | 'protocol_not_initialized'
  | 'render_unavailable'
  | 'selection_invalid'
  | 'status_failed'
  | 'unknown'

interface InlineGridEnvelope<Type extends InlineGridMessageType, Payload> {
  instanceId: string
  payload: Payload
  protocol: typeof INLINE_GRID_PROTOCOL
  type: Type
}

export type InlineGridHelloMessage = InlineGridEnvelope<
  typeof INLINE_GRID_MESSAGE_TYPE.HELLO,
  { campaignId: string }
>

export type InlineGridInitializeMessage = InlineGridEnvelope<
  typeof INLINE_GRID_MESSAGE_TYPE.INITIALIZE,
  { campaignId: string }
>

export type InlineGridReadyMessage = InlineGridEnvelope<
  typeof INLINE_GRID_MESSAGE_TYPE.READY,
  { campaignId: string }
>

export type InlineGridAnalyticsMessage = InlineGridEnvelope<
  typeof INLINE_GRID_MESSAGE_TYPE.ANALYTICS,
  InlineGridAnalyticsPayload
>

export type InlineGridResizeMessage = InlineGridEnvelope<
  typeof INLINE_GRID_MESSAGE_TYPE.RESIZE,
  { height: number }
>

export type InlineGridSubmitMessage = InlineGridEnvelope<
  typeof INLINE_GRID_MESSAGE_TYPE.SUBMIT,
  InlineGridSelection & { campaignId: string }
>

export type InlineGridErrorMessage = InlineGridEnvelope<
  typeof INLINE_GRID_MESSAGE_TYPE.ERROR,
  {
    code: InlineGridErrorCode
    phase: InlineGridErrorPhase
    reason?: InlineGridErrorReason
  }
>

export type InlineGridSelectionUpdateMessage = InlineGridEnvelope<
  typeof INLINE_GRID_MESSAGE_TYPE.SELECTION_UPDATE,
  InlineGridSelectionUpdate
>

export type InlineGridMessageFromGrid =
  | InlineGridAnalyticsMessage
  | InlineGridHelloMessage
  | InlineGridReadyMessage
  | InlineGridResizeMessage
  | InlineGridSubmitMessage
  | InlineGridErrorMessage
  | InlineGridEnvelope<typeof INLINE_GRID_MESSAGE_TYPE.SELECTION_UPDATE, InlineGridLiveSelection>

export type InlineGridMessageToGrid = InlineGridInitializeMessage | InlineGridSelectionUpdateMessage

const FREQUENCIES = new Set<InlineGridFrequency>([
  'one-time',
  'daily',
  'weekly',
  'bi-weekly',
  'monthly',
  'quarterly',
  'semi-annually',
  'yearly',
])

// Current Whammy emits GRID_NOT_INITIALIZED, GRID_RESIZE_FAILED, and GRID_SUBMIT_FAILED.
// Accept the remaining known codes so mixed deployments continue to fail safely.
const ERROR_CODES = new Set<InlineGridErrorCode>([
  'GRID_INITIALIZATION_FAILED',
  'GRID_NOT_INITIALIZED',
  'GRID_RENDER_FAILED',
  'GRID_RESIZE_FAILED',
  'GRID_SUBMIT_FAILED',
])

const ERROR_PHASES = new Set<InlineGridErrorPhase>(['initialize', 'render', 'resize', 'submit'])

const ERROR_REASONS = new Set<InlineGridErrorReason>([
  'campaign_unavailable',
  'configuration_unavailable',
  'height_unavailable',
  'protocol_not_initialized',
  'render_unavailable',
  'selection_invalid',
  'status_failed',
  'unknown',
])

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const isBoundedString = (value: unknown, maxLength: number): value is string =>
  typeof value === 'string' && value.length > 0 && value.length <= maxLength

const isCampaignId = (value: unknown): value is string => isBoundedString(value, 100)

const isInstanceId = (value: unknown): value is string =>
  isBoundedString(value, 200) && /^[A-Za-z0-9._:-]+$/.test(value)

const isCurrency = (value: unknown): value is string =>
  typeof value === 'string' && /^[A-Z]{3}$/.test(value)

const isAmount = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0

const isDesignation = (value: unknown): value is number =>
  typeof value === 'number' && Number.isSafeInteger(value) && value > 0

const isDedication = (value: unknown): value is InlineGridDedication =>
  isRecord(value) &&
  hasOnlyKeys(value, ['text', 'type']) &&
  typeof value.text === 'string' &&
  value.text.length > 0 &&
  value.text.length <= 100 &&
  (value.type === 'honor' || value.type === 'memory')

const isCalendarDate = (value: unknown): value is string => {
  if (typeof value !== 'string') {
    return false
  }

  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) {
    return false
  }

  const year = Number(match[1])
  const month = Number(match[2])
  const day = Number(match[3])

  if (year === 0 || month < 1 || month > 12) {
    return false
  }

  const isLeapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0)
  const daysInMonth = [31, isLeapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]

  return day >= 1 && day <= daysInMonth[month - 1]
}

const isRecurringEndDateUpdate = (value: unknown): value is string =>
  value === '' || isCalendarDate(value)

const isFrequency = (value: unknown): value is InlineGridFrequency =>
  typeof value === 'string' && FREQUENCIES.has(value as InlineGridFrequency)

function hasOnlyKeys(value: Record<string, unknown>, allowedKeys: string[]): boolean {
  return Object.keys(value).every((key) => allowedKeys.includes(key))
}

const hasValidBaseEnvelope = (value: unknown): value is Record<string, unknown> => {
  if (!isRecord(value)) {
    return false
  }

  return (
    value.protocol === INLINE_GRID_PROTOCOL &&
    isInstanceId(value.instanceId) &&
    typeof value.type === 'string' &&
    isRecord(value.payload)
  )
}

const hasValidSelection = (payload: Record<string, unknown>): boolean =>
  isAmount(payload.amount) &&
  isCurrency(payload.currency) &&
  isFrequency(payload.frequency) &&
  (payload.dedication === undefined || isDedication(payload.dedication)) &&
  (payload.designation === undefined || isDesignation(payload.designation)) &&
  (payload.recurringEndDate === undefined ||
    (payload.frequency !== 'one-time' && isCalendarDate(payload.recurringEndDate)))

const isNonNegativeAmount = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0

const hasValidAnalyticsPayload = (payload: Record<string, unknown>): boolean => {
  if (!isCampaignId(payload.campaignId) || typeof payload.action !== 'string') {
    return false
  }

  switch (payload.action) {
    case 'amount_change':
      return (
        hasOnlyKeys(payload, [
          'action',
          'campaignId',
          'currency',
          'donationType',
          'fromAmount',
          'toAmount',
        ]) &&
        isCurrency(payload.currency) &&
        (payload.donationType === 'custom' || payload.donationType === 'preset') &&
        isNonNegativeAmount(payload.fromAmount) &&
        isNonNegativeAmount(payload.toAmount)
      )
    case 'currency_change':
      return (
        hasOnlyKeys(payload, ['action', 'campaignId', 'fromCurrency', 'toCurrency']) &&
        isCurrency(payload.fromCurrency) &&
        isCurrency(payload.toCurrency)
      )
    case 'frequency_change':
      return (
        hasOnlyKeys(payload, ['action', 'campaignId', 'fromFrequency', 'toFrequency']) &&
        isFrequency(payload.fromFrequency) &&
        isFrequency(payload.toFrequency)
      )
    case 'interaction':
      return (
        hasOnlyKeys(payload, ['action', 'campaignId', 'trackingId']) &&
        typeof payload.trackingId === 'string' &&
        Object.prototype.hasOwnProperty.call(INLINE_GRID_INTERACTIONS, payload.trackingId)
      )
    case 'viewed':
      return (
        hasOnlyKeys(payload, [
          'action',
          'campaignId',
          'donation_amount_default',
          'donation_amount_options',
          'donation_currency_default',
          'donation_currency_options',
          'donation_frequency_default',
          'donation_frequency_options',
        ]) &&
        isNonNegativeAmount(payload.donation_amount_default) &&
        typeof payload.donation_amount_options === 'string' &&
        payload.donation_amount_options.length <= 2000 &&
        isCurrency(payload.donation_currency_default) &&
        typeof payload.donation_currency_options === 'string' &&
        payload.donation_currency_options.length <= 2000 &&
        isFrequency(payload.donation_frequency_default) &&
        typeof payload.donation_frequency_options === 'string' &&
        payload.donation_frequency_options.length <= 500
      )
    default:
      return false
  }
}

export const isInlineGridMessageFromGrid = (value: unknown): value is InlineGridMessageFromGrid => {
  if (!hasValidBaseEnvelope(value)) {
    return false
  }

  const payload = value.payload as Record<string, unknown>

  switch (value.type) {
    case INLINE_GRID_MESSAGE_TYPE.ANALYTICS:
      return hasValidAnalyticsPayload(payload)
    case INLINE_GRID_MESSAGE_TYPE.HELLO:
    case INLINE_GRID_MESSAGE_TYPE.READY:
      return hasOnlyKeys(payload, ['campaignId']) && isCampaignId(payload.campaignId)
    case INLINE_GRID_MESSAGE_TYPE.RESIZE:
      return (
        hasOnlyKeys(payload, ['height']) &&
        typeof payload.height === 'number' &&
        Number.isFinite(payload.height) &&
        payload.height > 0 &&
        payload.height <= 20000
      )
    case INLINE_GRID_MESSAGE_TYPE.SELECTION_UPDATE:
      return (
        hasOnlyKeys(payload, ['amount', 'currency', 'frequency']) &&
        (payload.amount === null || isAmount(payload.amount)) &&
        isCurrency(payload.currency) &&
        isFrequency(payload.frequency)
      )
    case INLINE_GRID_MESSAGE_TYPE.SUBMIT:
      return (
        hasOnlyKeys(payload, [
          'amount',
          'campaignId',
          'currency',
          'dedication',
          'designation',
          'frequency',
          'recurringEndDate',
        ]) &&
        isCampaignId(payload.campaignId) &&
        hasValidSelection(payload)
      )
    case INLINE_GRID_MESSAGE_TYPE.ERROR:
      return (
        hasOnlyKeys(payload, ['code', 'phase', 'reason']) &&
        typeof payload.code === 'string' &&
        ERROR_CODES.has(payload.code as InlineGridErrorCode) &&
        typeof payload.phase === 'string' &&
        ERROR_PHASES.has(payload.phase as InlineGridErrorPhase) &&
        (payload.reason === undefined ||
          (typeof payload.reason === 'string' &&
            ERROR_REASONS.has(payload.reason as InlineGridErrorReason)))
      )
    default:
      return false
  }
}

export const makeInlineGridMessage = <Type extends InlineGridMessageType, Payload>(
  type: Type,
  instanceId: string,
  payload: Payload
): InlineGridEnvelope<Type, Payload> => ({
  instanceId,
  payload,
  protocol: INLINE_GRID_PROTOCOL,
  type,
})

const parseOptionalNumber = (value: unknown): number | undefined => {
  if (typeof value !== 'string' && typeof value !== 'number') {
    return undefined
  }

  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined
}

export const toInlineGridSelectionUpdate = (params: Params): InlineGridSelectionUpdate | null => {
  const update: InlineGridSelectionUpdate = {}
  const amount = parseOptionalNumber(params.amount)
  const designation = parseOptionalNumber(params.designation)

  if (amount !== undefined) {
    update.amount = amount
  }
  if (typeof params.currency === 'string' && isCurrency(params.currency)) {
    update.currency = params.currency
  }
  if (params.dedication === '') {
    update.dedication = null
  } else if (typeof params.dedication === 'string') {
    const dedication = {
      text: params.dedication,
      type: params.dedicationType === 'memory' ? 'memory' : 'honor',
    } as const

    if (isDedication(dedication)) {
      update.dedication = dedication
    }
  }
  if (designation !== undefined && isDesignation(designation)) {
    update.designation = designation
  }
  if (isFrequency(params.frequency)) {
    update.frequency = params.frequency
  }
  if (
    typeof params.recurringEndDate === 'string' &&
    isRecurringEndDateUpdate(params.recurringEndDate)
  ) {
    update.recurringEndDate = params.recurringEndDate
  }

  return Object.keys(update).length > 0 ? update : null
}

/**
 * Promote an update to a full selection so it can act as the diff baseline. Returns
 * `undefined` when the update does not carry a complete selection.
 */
export const toInlineGridSelection = (
  update: InlineGridSelectionUpdate
): InlineGridSelection | undefined => {
  if (update.amount === undefined || update.currency === undefined || !update.frequency) {
    return undefined
  }

  return {
    amount: update.amount,
    currency: update.currency,
    frequency: update.frequency,
    ...(update.designation !== undefined && { designation: update.designation }),
    ...(update.dedication ? { dedication: update.dedication } : {}),
    ...(update.recurringEndDate ? { recurringEndDate: update.recurringEndDate } : {}),
  }
}

/**
 * Compare one field of an update against the current selection. `dedication` is an object,
 * so it needs a value comparison, and an empty `recurringEndDate` means the same as none.
 */
export const isUnchangedSelectionField = (
  key: keyof InlineGridSelectionUpdate,
  update: InlineGridSelectionUpdate,
  current: InlineGridSelection
): boolean => {
  if (key === 'dedication') {
    const next = update.dedication
    const previous = current.dedication

    if (next === null || next === undefined) {
      return previous === undefined
    }

    return previous !== undefined && previous.text === next.text && previous.type === next.type
  }

  if (key === 'recurringEndDate') {
    return (update.recurringEndDate || undefined) === (current.recurringEndDate || undefined)
  }

  return update[key] === current[key]
}
